import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import moment from 'moment';
import styled from 'styled-components';


// const generateRandomColor = () => {
//   const letters = '0123456789ABCDEF';
//   let color = '#';
//   for (let i = 0; i < 6; i++) {
//     color += letters[Math.floor(Math.random() * 16)];
//   }
//   console.log(color, 'color')
//   return color;
// };

const baseColors = new Set();
const baseColorList = [];
const colorUsageMap = new Map(); 

const generateRandomColor = () => {
  // Generate base hex color
  const createHexColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  // Convert hex to HSL
  const hexToHSL = (hex) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 7) {
      r = parseInt(hex.substring(1, 3), 16);
      g = parseInt(hex.substring(3, 5), 16);
      b = parseInt(hex.substring(5, 7), 16);
    }

    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
          break;
        case g:
          h = ((b - r) / d + 2) * 60;
          break;
        case b:
          h = ((r - g) / d + 4) * 60;
          break;
      }
    }

    return {
      h: Math.round(h),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // Generate variant from base HSL
  const generateVariantFromBase = (baseHex, variantIndex) => {
    const { h, s, l } = hexToHSL(baseHex);
    const delta = (variantIndex % 2 === 0 ? -1 : 1) * (variantIndex * 5); // Alternate between lighter and darker
    const newL = Math.max(10, Math.min(90, l + delta));
    return `hsl(${h}, ${s}%, ${newL}%)`;
  };

  if (baseColors.size < 100) {
    let newColor = createHexColor();
    while (baseColors.has(newColor)) {
      newColor = createHexColor();
    }
    baseColors.add(newColor);
    baseColorList.push(newColor);
    colorUsageMap.set(newColor, 0);
    return newColor;
  } else {
    
    const index = (baseColorList.length + [...colorUsageMap.values()].reduce((a, b) => a + b, 0)) % 100;
    const base = baseColorList[index];
    const usageCount = colorUsageMap.get(base) + 1;
    colorUsageMap.set(base, usageCount);

    const variant = generateVariantFromBase(base, usageCount);
    return variant;
  }
};


const getUniqueCampaignsAndStatuses = (data) => {
  const campaignsAndStatuses = new Set();
  data.forEach((entry) => {
    if(entry.campaignDetails) {
    entry.campaignDetails.forEach((detail) => {
      const key = `${detail.campaign || 'null-campaign'}-${detail.status}`;
      campaignsAndStatuses.add(key);
    });
  }
  });
  return Array.from(campaignsAndStatuses);
};

const transformData = (data) => {
  const uniqueKeys = getUniqueCampaignsAndStatuses(data);
  const transformed = data.map((entry) => {
    const newEntry = { date: entry.date };
    uniqueKeys.forEach((key) => {
      newEntry[key] = 0;
    });
   if(entry.campaignDetails) {
 entry.campaignDetails.forEach((detail) => {
      const key = `${detail.campaign || 'null-campaign'}-${detail.status}`;
      newEntry[key] = detail.count;
    });
   }
   
    return newEntry;
  });
  return transformed;
};

const LabelContainer = styled.div`
    background-color: #E6E9F1;
    border-radius: 3px;
    width: auto;
    padding: 15px 10px;
`
const LabelDate = styled.p`
    margin: 0 0 6px 0;
    color: #00AEEF;
    font-size: 12px;
    line-height: 12.57px;
    font-weight: 700;
    font-family: Roboto Condensed;
    text-align: left;
`

const StatsContainer = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    margin: 3px 0px
`
const Square = styled.span`
    width: 15px;
    height: 15px;
    border-radius: 5px;
    margin-right: 5px
    background-color: ${props => props.bgcolor}
`
const Stats = styled.p`
    margin: 0px
    color: #000000;
    font-weight: 400;
    font-size: 11px;
    line-height: 13px;
`

const Labelheader = styled.p`
    margin: 0 0 6px 0;
    font-size: 10px;
    line-height: 12.57px;
    font-weight: 700;
    font-family: Roboto Condensed;
    text-align: left;
`

const calculateTotalCounts = (data) => {
  const totals = {};
  data.forEach((entry) => {
if(entry.campaignDetails) {
    entry.campaignDetails.forEach((detail) => {
      const key = `${detail.campaign || 'null-campaign'}-${detail.status}`;
      if (!totals[key]) {
        totals[key] = 0;
      }
      totals[key] += detail.count;
    });
  }
  });
  return totals;
};

const DynamicLineChart = ({analyticsData, statusValue}) => {

  const transformedData = transformData(analyticsData);
  const uniqueKeys = getUniqueCampaignsAndStatuses(analyticsData);
    const totalCounts = calculateTotalCounts(analyticsData);
  const totalSentCount = analyticsData.reduce((acc, curr) => acc + curr.sent, 0);

  const keyColors = {};
  uniqueKeys.forEach((key) => {
    keyColors[key] = generateRandomColor();
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <LabelContainer
        >
           <LabelDate>{`Date: ${moment(label).format('MMM DD, YYYY')}`}</LabelDate>
          <Labelheader>
            status - campaign title
          </Labelheader>
          {payload.map((p, index) => (
            <StatsContainer key={index} style={{ color: p.stroke }}>
               <Square bgcolor={p.stroke} />
                                        <Stats>
                            {`${p.name}: ${p.value}`}
                          </Stats>
            </StatsContainer>
          ))}
        </LabelContainer>
      );
    }
    return null;
  };

  const legendFormatter = (value, entry, index) => {
    const dataKey = entry.payload.dataKey;
    const isTotalSent = dataKey === 'sent';

    if (isTotalSent) {
      return `${value} (${totalSentCount})`;
    }
    
    const totalCount = totalCounts[dataKey] || 0;
    return `${value} (${totalCount})`;
  };

  return (
    <ResponsiveContainer width="100%" height={250}>
    <LineChart
      width={730}
      height={250}
      data={transformedData}
      margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        dataKey="date"
        tickFormatter={(value) => moment(value).format("MM/DD")}
      />
      <YAxis allowDecimals={false} />
      <Tooltip content={<CustomTooltip />} />
      <Legend iconType="square" formatter={legendFormatter}
 />
      {uniqueKeys.map((key, index) => {
        const [campaignName, status] = key.split('-');
        const name = `${statusValue} -${campaignName === 'null' ? 'campaign not found' : campaignName}`;
        return (
          <Line
            key={index}
            type="monotone"
            dataKey={key}
            name={name}
            stroke={keyColors[key]}
            dot={{ stroke: keyColors[key], strokeWidth: 6 }}
            strokeWidth={2}
          />
        );
      })}
    </LineChart>
    </ResponsiveContainer>
  );
};

export default DynamicLineChart;